import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Row, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import InstitutionLogo from '@/components/common/InstitutionLogo';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { INSTITUTIONS } from '@/shared/constants/institutions';
import {
    REQUEST_OPEN_STATUSES,
    REQUEST_STATUS_LABELS,
    REQUEST_STATUS_VARIANTS,
} from '@/shared/constants/requests';
import requestService from '@/shared/services/requestService';

// The route caps a page at 100 and pages with limit and offset, so a page is
// a read rather than a slice of a list the browser already holds.
const PAGE_SIZE = 25;

const DAY_MS = 24 * 60 * 60 * 1000;

// The two statuses something is still owed on. The tab opens here because the
// question it answers is what has jammed, and a declined request is finished
// business; every status remains one choice away.
const LIVE_STATUSES = REQUEST_OPEN_STATUSES.join(',');

// Mirrors the minAgeDays ceiling in listQuerySchema. These are the spans a
// coordinator actually chases, and every one of them is well inside it.
const AGE_CHOICES = Object.freeze(['3', '7', '14', '30']);

const EMPTY_FILTERS = Object.freeze({
    status: LIVE_STATUSES,
    minAgeDays: '',
    sort: 'oldest',
});

const EMPTY_FILTERED = 'No request matches those filters. Try clearing one.';
const EMPTY_UNFILTERED = 'Nobody has requested anything yet.';

// An absent status reads as every status on the route and an absent
// minAgeDays as any age, so a blank box is dropped rather than sent. Sort
// always travels, because the default it would fall back to is the one the
// box already shows.
function toParams(filters, offset) {
    const params = { sort: filters.sort, limit: PAGE_SIZE, offset };
    if (filters.status) {
        params.status = filters.status;
    }
    if (filters.minAgeDays) {
        params.minAgeDays = Number(filters.minAgeDays);
    }
    return params;
}

const requestCount = (n) => `${n} ${n === 1 ? 'request' : 'requests'}`;

const messageCount = (n) => `${n} ${n === 1 ? 'message' : 'messages'}`;

// Age is the column a coordinator scans, so it is a count of whole days
// rather than a date to subtract in their head.
function ageLabel(value) {
    const days = Math.floor((Date.now() - new Date(value).getTime()) / DAY_MS);
    if (days <= 0) return 'Today';
    return days === 1 ? '1 day' : `${days} days`;
}

/**
 * One member of the pair, named.
 *
 * A member the projection did not resolve still gets a row: the request is
 * the thing being triaged, and it outlives either account.
 */
function MemberCell({ member }) {
    const person = member || {};
    return (
        <td>
            <div>{person.name || 'Unknown member'}</div>
            <div className="small">
                {/* InstitutionLogo renders nothing for a key it does not
                    know, so an institution added on the server before the
                    client mirror carries it shows as its raw key rather than
                    blanking the cell. */}
                {INSTITUTIONS[person.institution] ? (
                    <InstitutionLogo institution={person.institution} />
                ) : (
                    person.institution
                )}
                {person.department && (
                    <span className="text-muted ms-1">{person.department}</span>
                )}
            </div>
        </td>
    );
}

function AgeCell({ value }) {
    return (
        <td className="afe-nowrap">
            <time dateTime={value}>{ageLabel(value)}</time>
        </td>
    );
}

/** How the conversation is going, without any of what was said in it. */
function ThreadCell({ count, lastAt }) {
    if (!count) {
        return <td className="text-muted afe-nowrap">No messages</td>;
    }
    return (
        <td className="afe-nowrap">
            <div>{messageCount(count)}</div>
            <time className="text-muted small" dateTime={lastAt}>
                {`Last ${new Date(lastAt).toLocaleDateString()}`}
            </time>
        </td>
    );
}

/**
 * Triage names both members. A report names nobody.
 *
 * A coordinator chasing a request that has sat for three weeks has to know
 * who asked and who is sitting on it, so both are columns here. The reports
 * surface carries no member identity at all, because those figures are shown
 * to a facilities director and who asked for what is no part of that case.
 * Both rules are deliberate and they point opposite ways: the reports
 * projection must not be reused to build this table, and this table's shape
 * must not be reused in a report or an export.
 *
 * What this table does not carry is either party's address or phone number,
 * and nothing from the message thread but its size and its last instant. The
 * route projects it that way; a column added here that needs more is a change
 * to that projection and to what a list of every request in the exchange
 * exposes.
 */
function RequestsTab() {
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    // Applied separately from the form state, so a choice takes effect on
    // submit rather than firing a request per change.
    const [applied, setApplied] = useState(EMPTY_FILTERS);
    const [offset, setOffset] = useState(0);
    const [requests, setRequests] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = useCallback(async (active, from) => {
        setLoading(true);
        setError('');
        try {
            const data = await requestService.list(toParams(active, from));
            setRequests(data.requests || []);
            setTotal(data.total || 0);
        } catch (_error) {
            setRequests([]);
            setTotal(0);
            setError(
                'Could not load the requests. Check your connection and try' +
                    ' again.'
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load(applied, offset);
    }, [applied, offset, load]);

    const set = (key) => (event) =>
        setFilters({ ...filters, [key]: event.target.value });

    // A new filter starts at the first page, or a filter applied from page
    // three reads as empty when it has matches. The copy is what makes Apply
    // filters a refresh as well: a triage table has to pick up what another
    // admin changed, and passing the same object through would not re-read.
    const handleApply = (event) => {
        event.preventDefault();
        setApplied({ ...filters });
        setOffset(0);
    };

    const filtered = Boolean(applied.status) || Boolean(applied.minAgeDays);

    return (
        <>
            <h2 className="h4 mb-3">
                Requests{' '}
                <span className="text-muted fs-6">{requestCount(total)}</span>
            </h2>
            <Form onSubmit={handleApply} className="mb-3">
                <Row className="g-2 align-items-end">
                    <Col md={4}>
                        <Form.Group controlId="admin-requests-status">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                value={filters.status}
                                onChange={set('status')}
                            >
                                <option value={LIVE_STATUSES}>
                                    Open and accepted
                                </option>
                                <option value="">All statuses</option>
                                {Object.entries(REQUEST_STATUS_LABELS).map(
                                    ([key, label]) => (
                                        <option key={key} value={key}>
                                            {label}
                                        </option>
                                    )
                                )}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group controlId="admin-requests-age">
                            <Form.Label>Waiting at least</Form.Label>
                            <Form.Select
                                value={filters.minAgeDays}
                                onChange={set('minAgeDays')}
                            >
                                <option value="">Any age</option>
                                {AGE_CHOICES.map((days) => (
                                    <option key={days} value={days}>
                                        {`${days} days`}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group controlId="admin-requests-sort">
                            <Form.Label>Sort</Form.Label>
                            <Form.Select
                                value={filters.sort}
                                onChange={set('sort')}
                            >
                                <option value="oldest">Oldest first</option>
                                <option value="newest">Newest first</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={2}>
                        <Button type="submit" className="w-100">
                            Apply filters
                        </Button>
                    </Col>
                </Row>
            </Form>
            {error && <Alert variant="danger">{error}</Alert>}
            {loading && <LoadingSpinner message="Loading requests" />}
            {!loading && !error && (
                <>
                    {requests.length === 0 && offset === 0 && (
                        <p className="text-muted">
                            {filtered ? EMPTY_FILTERED : EMPTY_UNFILTERED}
                        </p>
                    )}
                    {requests.length > 0 && (
                        <Table hover responsive className="align-middle">
                            <thead>
                                <tr>
                                    <th scope="col">Request</th>
                                    <th scope="col">Requested by</th>
                                    <th scope="col">Listing owner</th>
                                    <th scope="col">Status</th>
                                    <th scope="col">Waiting</th>
                                    <th scope="col">Thread</th>
                                </tr>
                            </thead>
                            <tbody>
                                {requests.map((item) => {
                                    const listing = item.listing || {};
                                    const title =
                                        listing.title || 'Listing unavailable';
                                    return (
                                        <tr key={item._id}>
                                            <td className="afe-col-title">
                                                <Link
                                                    to={`/requests/${item._id}`}
                                                    aria-label={`Open the request for ${title}`}
                                                >
                                                    {title}
                                                </Link>
                                            </td>
                                            <MemberCell
                                                member={item.requester}
                                            />
                                            <MemberCell
                                                member={listing.owner}
                                            />
                                            <td className="afe-nowrap">
                                                <Badge
                                                    bg={
                                                        REQUEST_STATUS_VARIANTS[
                                                            item.status
                                                        ]
                                                    }
                                                    className="afe-table-badge"
                                                >
                                                    {REQUEST_STATUS_LABELS[
                                                        item.status
                                                    ] || item.status}
                                                </Badge>
                                            </td>
                                            <AgeCell value={item.createdAt} />
                                            <ThreadCell
                                                count={item.messageCount}
                                                lastAt={item.lastMessageAt}
                                            />
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    )}
                    {/* The pager outlives an empty page, because a result set
                        that shrank between reads leaves the admin past the
                        end and changing a filter resets the offset, so
                        dropping it here is a dead end. */}
                    {(requests.length > 0 || offset > 0) && (
                        <nav
                            aria-label="Request pages"
                            className="d-flex align-items-center gap-3"
                        >
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={offset === 0}
                                onClick={() =>
                                    setOffset(Math.max(0, offset - PAGE_SIZE))
                                }
                            >
                                Previous page
                            </Button>
                            <span className="text-muted small">
                                {requests.length > 0
                                    ? `Showing ${offset + 1} to ` +
                                      `${offset + requests.length} of ` +
                                      requestCount(total)
                                    : 'Nothing on this page. ' +
                                      `${requestCount(total)} in all.`}
                            </span>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={
                                    requests.length === 0 ||
                                    offset + requests.length >= total
                                }
                                onClick={() => setOffset(offset + PAGE_SIZE)}
                            >
                                Next page
                            </Button>
                        </nav>
                    )}
                </>
            )}
        </>
    );
}

export default RequestsTab;
